/////////////////////////////////////////////////////////
// Configurations related to user.                     //
/////////////////////////////////////////////////////////

// Note: The userFields come from userFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

import * as validators from '../util/validators';

/**
 * Configuration options for user fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public', 'protected', or 'private'.
 *                                  Default value: 'public'.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs on
 *                                  ProfileSettingsPage and AuthenticationPage.
 * - showConfig:                    Configuration for rendering user information. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - displayInProfile (optional):   Can be used to hide field content from profile page.
 *                                    Default value: true.
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for users to fill
 *   - requiredMessage (optional):    Message for mandatory fields.
 *   - displayInSignUp (optional):    Can be used to show field input on sign up page.
 *                                    Default value: true.
 * - userTypeConfig:                Configuration for limiting user field to specific user types.
 *   - limitToUserTypeIds:            Can be used to determine whether to limit the field to certain user types. The
 *                                    Console based asset configurations do not yet support user types, so in hosted configurations
 *                                    the default value for this is 'false'.
 *   - userTypeIds:                   An array of user types for which the extended
 *   (optional)                       data is relevant and should be added.
 */
export const userFields = [
  // ============ FARMER FIELDS ============

  // Farm/Project Name (displays on profile)
  {
    key: 'farmName',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.farmNameLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.farmNameLabel',
      placeholderMessage: 'ProfileSettingsForm.farmNamePlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Location: Address
  {
    key: 'address',
    scope: 'public',
    schemaType: 'location',
    showConfig: {
      label: 'ProfileSettingsForm.addressLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.addressLabel',
      placeholderMessage: 'ProfileSettingsForm.addressPlaceholder',
      isRequired: true,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },
  // Operation Area/Location
  {
    key: 'operationArea',
    scope: 'public',
    schemaType: 'location',
    showConfig: {
      label: 'ProfileSettingsForm.operationAreaLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.operationAreaLabel',
      placeholderMessage: 'ProfileSettingsForm.operationAreaPlaceholder',
      isRequired: true,
      displayInSignUp: true,
      requiredMessage: 'ProfileSettingsForm.operationAreaRequired',
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },
  // Street Address
  {
    key: 'street',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.streetLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.streetLabel',
      placeholderMessage: 'ProfileSettingsForm.streetPlaceholder',
      isRequired: true,
      displayInSignUp: true,
      requiredMessage: 'ProfileSettingsForm.streetRequired',
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // City
  {
    key: 'city',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.cityLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.cityLabel',
      placeholderMessage: 'ProfileSettingsForm.cityPlaceholder',
      isRequired: true,
      displayInSignUp: true,
      requiredMessage: 'ProfileSettingsForm.cityRequired',
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Postal Code
  {
    key: 'postalCode',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.postalCodeLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.postalCodeLabel',
      placeholderMessage: 'ProfileSettingsForm.postalCodePlaceholder',
      isRequired: true,
      displayInSignUp: true,
      requiredMessage: 'ProfileSettingsForm.postalCodeRequired',
      validate: validators.isNumber('ProfileSettingsForm.postalCodeInvalid'),
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Province
  {
    key: 'province',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.provinceLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.provinceLabel',
      placeholderMessage: 'ProfileSettingsForm.provincePlaceholder',
      isRequired: true,
      displayInSignUp: true,
      requiredMessage: 'ProfileSettingsForm.provinceRequired',
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },


  // Type of Cultivation
  {
    key: 'cultivationType',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      {
        option: 'certified_organic',
        label: 'ProfileSettingsForm.cultivationType.certifiedOrganic',
      },
      {
        option: 'organic_uncertified',
        label: 'ProfileSettingsForm.cultivationType.organicUncertified',
      },
      { option: 'traditional', label: 'ProfileSettingsForm.cultivationType.traditional' },
      { option: 'other', label: 'ProfileSettingsForm.cultivationType.other' },
    ],
    showConfig: {
      label: 'ProfileSettingsForm.cultivationTypeLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.cultivationTypeLabel',
      placeholderMessage: 'ProfileSettingsForm.cultivationTypePlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Organic Certificate Status
  {
    key: 'organicCertificate',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'yes', label: 'ProfileSettingsForm.organicCertificate.yes' },
      { option: 'no', label: 'ProfileSettingsForm.organicCertificate.no' },
      { option: 'in_process', label: 'ProfileSettingsForm.organicCertificate.inProcess' },
    ],
    showConfig: {
      label: 'ProfileSettingsForm.organicCertificateLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.organicCertificateLabel',
      placeholderMessage: 'ProfileSettingsForm.organicCertificatePlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Certificate Number or Image URL
  {
    key: 'certificateInfo',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.certificateInfoLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.certificateInfoLabel',
      placeholderMessage: 'ProfileSettingsForm.certificateInfoPlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },
  // Pickup Days (multi-select)
  {
    key: 'pickupDays',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'monday', label: 'ProfileSettingsForm.pickupDays.monday' },
      { option: 'tuesday', label: 'ProfileSettingsForm.pickupDays.tuesday' },
      { option: 'wednesday', label: 'ProfileSettingsForm.pickupDays.wednesday' },
      { option: 'thursday', label: 'ProfileSettingsForm.pickupDays.thursday' },
      { option: 'friday', label: 'ProfileSettingsForm.pickupDays.friday' },
      { option: 'saturday', label: 'ProfileSettingsForm.pickupDays.saturday' },
      { option: 'sunday', label: 'ProfileSettingsForm.pickupDays.sunday' },
    ],
    showConfig: {
      label: 'ProfileSettingsForm.pickupDaysLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.pickupDaysLabel',
      placeholderMessage: 'ProfileSettingsForm.pickupDaysPlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Pickup Start Time
  {
    key: 'pickupStartTime',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.pickupStartTimeLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.pickupStartTimeLabel',
      placeholderMessage: 'ProfileSettingsForm.pickupStartTimePlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Pickup End Time
  {
    key: 'pickupEndTime',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.pickupEndTimeLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.pickupEndTimeLabel',
      placeholderMessage: 'ProfileSettingsForm.pickupEndTimePlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Same-day Pickup Available
  {
    key: 'sameDayPickup',
    scope: 'public',
    schemaType: 'boolean',
    showConfig: {
      label: 'ProfileSettingsForm.sameDayPickupLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.sameDayPickupLabel',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },
  // Allow Farm Visits
  {
    key: 'allowFarmVisits',
    scope: 'public',
    schemaType: 'boolean',
    showConfig: {
      label: 'ProfileSettingsForm.allowFarmVisitsLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.allowFarmVisitsLabel',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },
  {
    key: 'visitPriceType',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'free', label: 'ProfileSettingsForm.visitPriceType.free' },
      { option: '5euro', label: 'ProfileSettingsForm.visitPriceType.5euro' },
      { option: 'custom', label: 'ProfileSettingsForm.visitPriceType.custom' },
    ],
    showConfig: {
      label: 'ProfileSettingsForm.visitPriceTypeLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.visitPriceTypeLabel',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },
  {
    key: 'customVisitPrice',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.customVisitPriceLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.customVisitPriceLabel',
      placeholderMessage: 'ProfileSettingsForm.customVisitPricePlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Personal Mini-Slogan
  {
    key: 'miniSlogan',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.miniSloganLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.miniSloganLabel',
      placeholderMessage: 'ProfileSettingsForm.miniSloganPlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Website URL
  {
    key: 'websiteUrl',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.websiteUrlLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.websiteUrlLabel',
      placeholderMessage: 'ProfileSettingsForm.websiteUrlPlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Instagram URL
  {
    key: 'instagramUrl',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.instagramUrlLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.instagramUrlLabel',
      placeholderMessage: 'ProfileSettingsForm.instagramUrlPlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Facebook URL
  {
    key: 'facebookUrl',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'ProfileSettingsForm.facebookUrlLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.facebookUrlLabel',
      placeholderMessage: 'ProfileSettingsForm.facebookUrlPlaceholder',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // Subscription Agreement Checkbox
  {
    key: 'subscriptionAgreement',
    scope: 'public',
    schemaType: 'boolean',
    showConfig: {
      label: 'ProfileSettingsForm.subscriptionAgreementLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.subscriptionAgreementLabel',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['farmer'],
    },
  },

  // ============ COLLABORATOR FIELDS ============

  // Type of Collaboration (multi-select dropdown)
  {
    key: 'collaborationTypes',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'pickup_point', label: 'Pickup point' },
      { option: 'promotion', label: 'Promotion (social media, channel, local media…)' },
      { option: 'friendly_shop', label: 'Friendly shop' },
      { option: 'activities_workshops', label: 'Activities or workshops' },
      { option: 'other', label: 'Other (free text)' },
    ],
    showConfig: {
      label: 'Tipos de colaboración',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.collaborationTypesLabel',
      placeholderMessage: 'ProfileSettingsForm.collaborationTypesPlaceholder',
      isRequired: false,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['collaborator'],
    },
  },

  // Website / Social Media Link (optional — for validation)
  {
    key: 'website',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Sitio web / Enlace de redes sociales',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.websiteLabel',
      placeholderMessage: 'ProfileSettingsForm.websitePlaceholder',
      isRequired: false,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['collaborator'],
    },
  },

  // Area or Operating Location (free text or map autocomplete)
  {
    key: 'location',
    scope: 'public',
    schemaType: 'location',
    showConfig: {
      label: 'ProfileSettingsForm.locationLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.locationLabel',
      placeholderMessage: 'ProfileSettingsForm.locationPlaceholder',
      isRequired: false,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['collaborator'],
    },
  },

  // Collaboration Message (test area - why do you want to collaborate? What motivates you?)
  {
    key: 'collaborationMessage',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: '¿Por qué quieres colaborar?',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.collaborationMessageLabel',
      placeholderMessage: 'ProfileSettingsForm.collaborationMessagePlaceholder',
      isRequired: false, // Changed to false - it's a test area
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['collaborator'],
    },
  },

  // Checkbox: I agree to collaborate voluntarily, without contractual obligations.
  {
    key: 'voluntaryAgreement',
    scope: 'public',
    schemaType: 'checkbox',
    showConfig: {
      label: 'ProfileSettingsForm.voluntaryAgreementLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.voluntaryAgreementLabel',
      isRequired: true,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['collaborator'],
    },
  },

  // Checkbox: I have read and accept collaboration conditions.
  {
    key: 'collaborationConditions',
    scope: 'public',
    schemaType: 'checkbox',
    showConfig: {
      label: 'ProfileSettingsForm.collaborationConditionsLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.collaborationConditionsLabel',
      isRequired: true,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['collaborator'],
    },
  },

  // Consumer Email Address (with validation and privacy note)
  {
    key: 'email',
    scope: 'public',
    schemaType: 'email',
    showConfig: {
      label: 'ProfileSettingsForm.emailLabel',
      displayInProfile: false, // Don't display email in profile for privacy
    },
    saveConfig: {
      label: 'ProfileSettingsForm.emailLabel',
      placeholderMessage: 'ProfileSettingsForm.emailPlaceholder',
      isRequired: true,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // Consumer Password (minimum 6 characters)
  {
    key: 'password',
    scope: 'private',
    schemaType: 'password',
    showConfig: {
      label: 'ProfileSettingsForm.passwordLabel',
      displayInProfile: false, // Never display password
    },
    saveConfig: {
      label: 'ProfileSettingsForm.passwordLabel',
      placeholderMessage: 'ProfileSettingsForm.passwordPlaceholder',
      isRequired: true,
      displayInSignUp: true,
      minLength: 6,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // Consumer Terms and Conditions
  {
    key: 'termsAndConditions',
    scope: 'public',
    schemaType: 'checkbox',
    showConfig: {
      label: 'ProfileSettingsForm.termsAndConditionsLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.termsAndConditionsLabel',
      isRequired: true,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // Consumer Receive Updates (optional)
  {
    key: 'receiveUpdates',
    scope: 'public',
    schemaType: 'checkbox',
    showConfig: {
      label: 'ProfileSettingsForm.receiveUpdatesLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.receiveUpdatesLabel',
      isRequired: false,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // ============ CONSUMER FIELDS ============

  // Consumer Email
  {
    key: 'email',
    scope: 'public',
    schemaType: 'email',
    showConfig: {
      label: 'ProfileSettingsForm.emailLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.emailLabel',
      placeholderMessage: 'ProfileSettingsForm.emailPlaceholder',
      isRequired: true,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // Consumer Password
  {
    key: 'password',
    scope: 'private',
    schemaType: 'password',
    showConfig: {
      label: 'ProfileSettingsForm.passwordLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.passwordLabel',
      placeholderMessage: 'ProfileSettingsForm.passwordPlaceholder',
      isRequired: true,
      displayInSignUp: true,
      minLength: 6,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // Consumer Location/Postal Code
  {
    key: 'consumerLocation',
    scope: 'public',
    schemaType: 'location',
    showConfig: {
      label: 'ProfileSettingsForm.consumerLocationLabel',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.consumerLocationLabel',
      placeholderMessage: 'ProfileSettingsForm.consumerLocationPlaceholder',
      isRequired: true,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // How did you hear about us?
  {
    key: 'hearAboutUs',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'social_media', label: 'ProfileSettingsForm.hearAboutUs.socialMedia' },
      { option: 'recommendation', label: 'ProfileSettingsForm.hearAboutUs.recommendation' },
      { option: 'event', label: 'ProfileSettingsForm.hearAboutUs.event' },
      { option: 'other', label: 'ProfileSettingsForm.hearAboutUs.other' },
    ],
    showConfig: {
      label: 'ProfileSettingsForm.hearAboutUsLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.hearAboutUsLabel',
      placeholderMessage: 'ProfileSettingsForm.hearAboutUsPlaceholder',
      isRequired: false,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // Terms and Conditions
  {
    key: 'termsAndConditions',
    scope: 'public',
    schemaType: 'checkbox',
    showConfig: {
      label: 'ProfileSettingsForm.termsAndConditionsLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.termsAndConditionsLabel',
      isRequired: true,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },

  // Receive Updates and Recipes
  {
    key: 'receiveUpdates',
    scope: 'public',
    schemaType: 'checkbox',
    showConfig: {
      label: 'ProfileSettingsForm.receiveUpdatesLabel',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'ProfileSettingsForm.receiveUpdatesLabel',
      isRequired: false,
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['consumer'],
    },
  },
];

/////////////////////////////////////
// Example user type configuration //
/////////////////////////////////////
/**
 * User types are not supported in hosted configuration yet.
 *
 * To take user types into use in your
 * custom code, you can do the following things:
 * - Add a new user field with key 'userType', scope 'publicData', and schemaType enum
 *  - Consider whether or not you want to allow your users to change their user type after first creating it
 * - Set your user types as the available options for the userType field
 * - Add your user types in the array below
 * - Update configHelpers.js mergeUserConfig to pass user types to the validUserFields function
 */

export const userTypes = [
  {
    userType: 'farmer',
    label: 'Farmer',
    roles: {
      customer: true, // Farmers can also buy
      provider: true, // Farmers can sell
    },
    defaultUserFields: {
      phoneNumber: true,
      displayName: true,
    },
    displayNameSettings: {
      displayInSignUp: true,
      required: false,
    },
    phoneNumberSettings: {
      displayInSignUp: true,
      required: true, // Phone required for farmers
    },
    accountLinksVisibility: {
      postListings: true, // Farmers can create listings
      payoutDetails: true,
      paymentMethods: true,
    },
  },
  {
    userType: 'consumer',
    label: 'Consumer',
    roles: {
      customer: true,
      provider: false, // Consumers can only buy, not sell
    },
    defaultUserFields: {
      displayName: true,
      email: true,
      password: true,
      consumerLocation: true,
      hearAboutUs: true,
      termsAndConditions: true,
      receiveUpdates: true,
    },
    displayNameSettings: {
      displayInSignUp: true,
      required: true, // Full name required
    },
    emailSettings: {
      displayInSignUp: true,
      required: true, // Email required
    },
    passwordSettings: {
      displayInSignUp: true,
      required: true, // Password required
    },
    consumerLocationSettings: {
      displayInSignUp: true,
      required: true, // Location required
    },
    hearAboutUsSettings: {
      displayInSignUp: true,
      required: false, // How did you hear about us - optional
    },
    termsAndConditionsSettings: {
      displayInSignUp: true,
      required: true, // Terms and conditions required
    },
    receiveUpdatesSettings: {
      displayInSignUp: true,
      required: false, // Receive updates - optional
    },
    accountLinksVisibility: {
      postListings: true, // Consumers cannot create listings
      payoutDetails: false,
      paymentMethods: true,
    },
  },
  {
    userType: 'collaborator',
    label: 'Collaborator',
    roles: {
      customer: true, // Collaborators can buy
      provider: false, // Collaborators cannot sell
    },
    defaultUserFields: {
      phoneNumber: true,
      displayName: true,
    },
    displayNameSettings: {
      displayInSignUp: true,
      required: false,
    },
    phoneNumberSettings: {
      displayInSignUp: true,
      required: false, // Phone optional for collaborators
    },
    accountLinksVisibility: {
      postListings: false, // Collaborators cannot create listings
      payoutDetails: false,
      paymentMethods: false,
    },
  },
];
